/**
 * Maps GET /spool-tracker/getAllSpools response bodies → plain objects matching `SpoolCard` shape
 * (used by saga only; no React / TS imports).
 */
import get from 'lodash/get';

const SPOOL_STATUSES = ['fat_complete', 'fat_in_progress', 'in_fabrication', 'not_started'];
const WELD_STATUSES = ['accepted', 'ndt_pass', 'welded', 'pending'];

function extractSpoolArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const d = payload.data;
    if (Array.isArray(d)) return d;
    if (d && typeof d === 'object') {
      if (Array.isArray(d.data)) return d.data;
      if (Array.isArray(d.recordList)) return d.recordList;
      if (Array.isArray(d.spools)) return d.spools;
      if (Array.isArray(d.rows)) return d.rows;
      if (Array.isArray(d.result)) return d.result;
      if (Array.isArray(d.items)) return d.items;
    }
    if (Array.isArray(payload.recordList)) return payload.recordList;
    if (Array.isArray(payload.spools)) return payload.spools;
    if (Array.isArray(payload.rows)) return payload.rows;
  }
  return [];
}

function normalizeSpoolStatus(raw) {
  const v = String(raw ?? 'not_started')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (SPOOL_STATUSES.includes(v)) return v;
  if (v === 'complete' || v === 'fatcomplete') return 'fat_complete';
  if (v === 'fat_in_prog' || v === 'fatinprogress') return 'fat_in_progress';
  if (v === 'fabrication' || v === 'in_progress' || v === 'active') return 'in_fabrication';
  return 'not_started';
}

function normalizeWeldStatus(raw) {
  const v = String(raw ?? 'pending').trim().toLowerCase();
  if (WELD_STATUSES.includes(v)) return v;
  if (v === 'pass' || v === 'ndt pass') return 'ndt_pass';
  if (v === 'complete' || v === 'welded_done') return 'welded';
  return 'pending';
}

/**
 * Coalesce API variants: backend returns camelCase (`sideADesc`); some paths use snake_case.
 */
function firstStr(...vals) {
  for (let i = 0; i < vals.length; i += 1) {
    const v = vals[i];
    if (v != null && v !== '') return String(v);
  }
  return undefined;
}

/** Passes through API `workflowRepair` / `workflow_repair` for VT/NDT repair cycles (see `repairWorkflowHelpers.ts`). */
/** Coerce one panel object or array → always an array (GET workflow: every step slot is `WeldStepPanelApiDto[]`). */
function toStepPanelArray(v) {
  if (Array.isArray(v)) return v;
  if (v != null && typeof v === 'object') return [v];
  return [];
}

/**
 * Normalize `weldStepPanels` so each step key is an array. Legacy single-object rows are wrapped as `[row]`.
 */
function normalizeWeldStepPanels(panels) {
  if (!panels || typeof panels !== 'object') return null;
  return {
    fitUp: toStepPanelArray(panels.fitUp),
    preheat: toStepPanelArray(panels.preheat),
    welding: toStepPanelArray(panels.welding),
    vt: toStepPanelArray(panels.vt),
    ndt: toStepPanelArray(panels.ndt),
    coating: toStepPanelArray(panels.coating),
    accept: toStepPanelArray(panels.accept),
  };
}

function normalizeWorkflowRepair(raw) {
  if (!raw || typeof raw !== 'object') return undefined;
  const phase = raw.phase ?? raw.phase_code ?? raw.phaseCode;
  const allowed = new Set(['awaiting_repair_weld', 'awaiting_vt_retest', 'awaiting_ndt_retest']);
  const p = phase != null ? String(phase).trim() : '';
  if (!allowed.has(p)) return undefined;
  const src = raw.sourceStep ?? raw.source_step ?? raw.source_step_type;
  let sourceStep;
  if (src === 'vt' || src === 'ndt') sourceStep = src;
  else if (src != null) {
    const s = String(src).toLowerCase();
    if (s.includes('visual') || s === 'vt') sourceStep = 'vt';
    else if (s.includes('ndt')) sourceStep = 'ndt';
  }
  const out = { phase: p };
  if (sourceStep) out.sourceStep = sourceStep;
  return out;
}

function mapWeldJoint(raw, index) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const id = String(r.id ?? r.weld_joint_id ?? r.joint_id ?? `wj-${index}`);
  const numberStr = String(
    r.number ?? r.joint_number ?? r.weld_number ?? r.jointNumber ?? `W-${index + 1}`
  );
  const welderRaw = firstStr(
    r.welder,
    r.welder_name,
    r.welderName,
    r.welderDisplay,
    r.welder_display
  );
  const out = {
    id,
    number: numberStr,
    type: String(r.type ?? r.joint_type ?? r.jointType ?? 'Butt'),
    welder: welderRaw != null && welderRaw !== '' ? welderRaw : '—',
    status: normalizeWeldStatus(r.status ?? r.weld_status ?? r.weldStatus),
    date: firstStr(r.date, r.weld_date, r.weldDate),
    wps: firstStr(r.wps, r.wps_ref, r.wpsRef),
    process: firstStr(r.process, r.weldingProcess, r.weldType),
    sideADesc: firstStr(r.sideADesc, r.side_a_desc, r.sideADescription),
    sideBDesc: firstStr(r.sideBDesc, r.side_b_desc, r.sideBDescription),
    sideAHeat: firstStr(r.sideAHeat, r.side_a_heat, r.heatA, r.sideAHeatNumber),
    sideBHeat: firstStr(r.sideBHeat, r.side_b_heat, r.heatB, r.sideBHeatNumber),
    consumableLot: firstStr(
      r.consumableLot,
      r.consumable_lot,
      r.lot
    ),
    ndtOutcomeLine: firstStr(r.ndtOutcomeLine, r.ndt_outcome_line),
    vtSummaryLine: firstStr(r.vtSummaryLine, r.vt_summary_line),
    fitUp: r.fitUp && typeof r.fitUp === 'object' ? r.fitUp : r.fit_up && typeof r.fit_up === 'object' ? r.fit_up : undefined,
    ndt: Array.isArray(r.ndt) ? r.ndt : r.ndt_data,
    sortOrder: r.sortOrder != null ? Number(r.sortOrder) : r.sort_order != null ? Number(r.sort_order) : undefined,
    /** Present when `pipe_id` is set on GET (mirrors `WeldJointGridItemWithSteps`). */
    workflowStatus:
      r.workflowStatus != null && String(r.workflowStatus).trim() !== ''
        ? String(r.workflowStatus).trim().toLowerCase()
        : undefined,
    /** Step-panel rows — every step is an array (0..n rows per joint). */
    weldStepPanels:
      r.weldStepPanels && typeof r.weldStepPanels === 'object'
        ? normalizeWeldStepPanels(r.weldStepPanels)
        : null,
    indication: r.indication,
    repair: r.repair,
    retest: r.retest,
    workflowRepair: normalizeWorkflowRepair(r.workflowRepair ?? r.workflow_repair),
  };
  return out;
}

function mapFat(raw) {
  const fat = raw.fat;
  if (fat && typeof fat === 'object' && !Array.isArray(fat)) {
    const done = Number(fat.done ?? fat.completed ?? 0);
    const total = Number(fat.total ?? fat.count ?? 0);
    if (total > 0) return { done: Math.min(Math.max(0, done), total), total };
  }
  const done = Number(raw.fat_done ?? raw.fatDone ?? 0);
  const total = Number(raw.fat_total ?? raw.fatTotal ?? 0);
  if (total > 0) return { done: Math.min(Math.max(0, done), total), total };
  return null;
}

function pct(raw, keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = raw[k];
    if (v !== undefined && v !== null && v !== '') {
      const n = Number(v);
      if (!Number.isNaN(n)) return Math.min(100, Math.max(0, n));
    }
  }
  return 0;
}

function jointsFromRow(raw) {
  const list =
    (Array.isArray(raw.weld_joints) ? raw.weld_joints : null) ??
    (Array.isArray(raw.weldJoints) ? raw.weldJoints : null) ??
    (Array.isArray(raw.joints) ? raw.joints : null) ??
    [];
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.map((j, i) => mapWeldJoint(j, i));
}

function mapRowToSpoolCard(raw, index) {
  const id = Number(raw.id ?? raw.spool_id ?? raw.spoolId ?? index + 1) || index + 1;
  const name = String(
    raw.name ?? raw.spool_name ?? raw.spoolName ?? raw.spool_number ?? raw.spoolNumber ?? `SPOOL-${index + 1}`
  );
  const status = normalizeSpoolStatus(raw.status ?? raw.spool_status ?? raw.spoolStatus);
  const weldJoints = jointsFromRow(raw);
  const welds = Number(raw.welds ?? raw.weld_count ?? raw.weldCount ?? weldJoints.length) || weldJoints.length;

  return {
    id,
    name,
    status,
    type: String(raw.type ?? raw.spool_type ?? 'Prefab'),
    shape: String(raw.shape ?? raw.spool_shape ?? 'Straight'),
    length: String(raw.length ?? raw.length_m ?? raw.lengthM ?? '—'),
    welds,
    welding: pct(raw, ['welding', 'welding_pct', 'weldingPct', 'welding_percent']),
    ndt: pct(raw, ['ndt', 'ndt_pct', 'ndtPct', 'ndt_percent']),
    coating: pct(raw, ['coating', 'coating_pct', 'coatingPct', 'coating_percent']),
    fat: mapFat(raw),
    weldJoints,
    pipeId: raw.pipeId != null ? Number(raw.pipeId) : raw.pipe_id != null ? Number(raw.pipe_id) : undefined,
    spoolDetails: raw.spoolDetails && typeof raw.spoolDetails === 'object' ? raw.spoolDetails : undefined,
  };
}

/**
 * @param {unknown} resBody - axios `response.data`
 * @returns {object[]} SpoolCard-shaped plain objects
 */
export function mapGetAllSpoolsResponse(resBody) {
  const nested = get(resBody, 'data', resBody);
  const rows = extractSpoolArray(nested);
  return rows.map((row, i) => mapRowToSpoolCard(row && typeof row === 'object' ? row : {}, i));
}
